import { makeAutoObservable, runInAction } from 'mobx';
import OrderService, { IFetchOrder } from '../../../services/order-service';
import { rootContainer, TYPE } from '../../../services/container/root';
import OrderStore from './order-store';

export class CreateRmaOrderLookupStore {
    private _searchOrderName: string = '';
    private _isSearching: boolean = false;
    private _searchError: string | null = null;
    private _orders: OrderStore[] = [];

    private get orderService(): OrderService {
        return rootContainer.get<OrderService>(TYPE.OrderService);
    }

    public get searchOrderName(): string {
        return this._searchOrderName;
    }

    public get isSearching(): boolean {
        return this._isSearching;
    }

    public get searchError(): string | null {
        return this._searchError;
    }

    public get orders(): OrderStore[] {
        return this._orders;
    }

    public get isMixedOrder(): boolean {
        return this._orders.length > 1;
    }

    public get majorOrder(): OrderStore | undefined {
        return this._orders[0];
    }

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public setSearchOrderName(value: string): void {
        this._searchOrderName = value;
    }

    public async fetchOrder(): Promise<OrderStore[]> {
        if (!this._searchOrderName.trim()) {
            return [];
        }

        this._isSearching = true;
        this._orders = [];

        try {
            const withEligibility = true;
            const useCache = false;
            const withRefunded = false;
            const withMixedOrderGroup = true;
            const response = await this.orderService.fetchOrder(
                this._searchOrderName,
                null,
                withEligibility,
                useCache,
                withRefunded,
                withMixedOrderGroup,
            );

            runInAction(() => {
                this._isSearching = false;
                if (response.error_message) {
                    this._setSearchError(response.error_message);
                    return;
                }
                this._buildOrders(response);
                this._setSearchError(null);
            });

            return this._orders;
        } catch (error) {
            console.error(error);
            runInAction(() => {
                this._isSearching = false;
                this._setSearchError(this._getSearchErrorText());
            });
            return [];
        }
    }

    private _buildOrders(response: IFetchOrder): void {
        const group = response.mixed_order_group;
        const members: IFetchOrder[] = (group?.length ?? 0) >= 2 ? group! : [response];
        this._orders = members.map((member) => new OrderStore(member, member.eligibility));
    }

    public setSearchError(value: string | null): void {
        this._setSearchError(value);
    }

    private _setSearchError(value: string | null): void {
        this._searchError = value;
    }

    private _getSearchErrorText(): string {
        return 'Order ' + this._searchOrderName + ' was not found';
    }
}
